'use client';

import type { PharmaVariantMetadata } from '@lib/types/pharma';
import { stripMatchingLeadingHeading } from '@lib/util/markdown-headings';
import { type MarkdownHeading, slugifyHeading } from '@lib/util/typography';
import MarkdownContent from '@modules/common/components/markdown-content';
import {
  getVariantFields,
  getVariantSections,
  mergeSections,
  type ValidatedSection,
} from '@modules/products/utils/variant-sections';
import { useEffect, useMemo, useState } from 'react';
import type { ProductVariantItem } from '../../../../types/view-models';

type Props = {
  variants: ProductVariantItem[];
  initialVariantId: string | undefined;
  /** Product-level sections — shown as fallback, replaced by matching variant sections. */
  productSections: ValidatedSection[];
};

const HEADING_CLASS =
  'font-heading font-normal text-2xl leading-[1.125] tracking-[-0.01em] text-brand-primary-900 scroll-mt-44 mb-4';

const FIELD_CONTENT_CLASS =
  'mt-0 prose-p:text-brand-primary-800 prose-p:text-base prose-p:leading-[1.5] prose-li:text-brand-primary-800 prose-strong:text-brand-primary-900';

// noHeadingShift=true keeps ##→<h2>, ###→<h3>, so size targets are h2/h3/h4
const SECTION_PROSE_CLASS =
  'mt-0 prose-headings:font-heading prose-headings:font-normal prose-headings:leading-[1.125] prose-headings:tracking-[-0.01em] prose-headings:text-brand-primary-900 prose-headings:scroll-mt-44 prose-h2:text-2xl prose-h3:text-xl prose-h4:text-lg prose-p:text-brand-primary-800 prose-p:text-base prose-p:leading-[1.5] prose-li:text-brand-primary-800 prose-strong:text-brand-primary-900';

const ProductVariantSections = ({
  variants,
  initialVariantId,
  productSections,
}: Props) => {
  const [activeVariantId, setActiveVariantId] = useState(initialVariantId);

  useEffect(() => {
    const handler = (e: Event) => {
      const variantId = (e as CustomEvent<{ variantId: string }>).detail
        .variantId;
      setActiveVariantId(variantId);
    };
    window.addEventListener('medusa:variantchanged', handler);
    return () => window.removeEventListener('medusa:variantchanged', handler);
  }, []);

  // `initialVariantId` is the first in-stock variant (see `getDefaultVariant`),
  // so content is visible on first paint; `medusa:variantchanged` overrides it
  // once the user makes an explicit selection.
  const activeVariant = variants.find((v) => v.id === activeVariantId);
  const meta = activeVariant?.metadata as PharmaVariantMetadata | undefined;

  const variantSections = useMemo(() => getVariantSections(meta), [meta]);
  const allSections = useMemo(
    () => mergeSections(variantSections, productSections),
    [variantSections, productSections],
  );

  const variantFields = useMemo(() => getVariantFields(meta), [meta]);

  // Dispatch headings in DOM render order so the TOC scroll-spy stays accurate.
  const currentHeadings = useMemo<MarkdownHeading[]>(() => {
    const fieldHeadings: MarkdownHeading[] = variantFields.map(({ field }) => ({
      text: field.label,
      id: field.id,
      level: 2,
    }));

    const sectionHeadings: MarkdownHeading[] = allSections.map((s) => ({
      text: s.label,
      id: slugifyHeading(s.label),
      level: 2,
    }));

    return [...fieldHeadings, ...sectionHeadings];
  }, [variantFields, allSections]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent<{ headings: MarkdownHeading[] }>(
        'medusa:variantheadingschanged',
        { detail: { headings: currentHeadings } },
      ),
    );
  }, [currentHeadings]);

  return (
    <>
      {variantFields.map(({ field, value }) => (
        <section key={field.id} aria-labelledby={field.id}>
          <h2 id={field.id} className={HEADING_CLASS}>
            {field.label}
          </h2>
          {field.key === 'pil_url' ? (
            <p className="text-brand-primary-800 text-base leading-[1.5]">
              <a
                href={value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-interactive-700 hover:text-brand-interactive-800 underline hover:no-underline"
              >
                View Patient Information Leaflet (PDF)
              </a>
            </p>
          ) : (
            <MarkdownContent content={value} className={FIELD_CONTENT_CLASS} />
          )}
        </section>
      ))}
      {allSections.map((section) => {
        const headingId = slugifyHeading(section.label);
        const bodyContent = stripMatchingLeadingHeading(
          section.content,
          section.label,
        );

        return (
          <section key={section.label} aria-labelledby={headingId}>
            <h2 id={headingId} className={HEADING_CLASS}>
              {section.label}
            </h2>
            <MarkdownContent
              content={bodyContent}
              className={SECTION_PROSE_CLASS}
              noHeadingShift
            />
          </section>
        );
      })}
    </>
  );
};

export default ProductVariantSections;
